import { Button } from '@carbon/react';
import { Fragment } from 'react';

import type {
  SlotInterestRow,
  SlotSubtract,
  StreamCode,
  TractDraft,
} from '../types';
import { isTractAmSelectable, tractKey } from '../utils';
import styles from './single-slot-content.module.scss';
import { TractConfirmSection } from './tract-confirm-section';

interface SingleSlotContentProps {
  row: SlotInterestRow;
  /** Streams certified per tract during this panel session (keyed by
   *  tractKey) — merged into each card's certified set before the table
   *  refetch lands. */
  sessionCertified: Record<string, StreamCode[]>;
  onConfirmTract: (subtract: SlotSubtract | null, draft: TractDraft) => void;
  submitting: boolean;
  /** "Set AM as Evidence Source for All" — certifies every AM-eligible tract
   *  of this slot to Allocation Manager in one submit. */
  onSetAmForAll: () => void;
}

/**
 * Single-slot Confirm Interest body, redesigned to the Select Evidence Source
 * mock: a "Set AM as Evidence Source for All" shortcut + helper text, then one
 * `TractConfirmSection` card per tract (each owning its evidence source, source
 * table, streams, supporting note, Confirm/Edit, and the On Hold overlay).
 * No-subtract slots render a single pseudo-tract card. All certify state binds
 * to the shared per-tract form from `useWorkspaceConfirmation`.
 *
 * @param {SingleSlotContentProps} props - Component props.
 * @return {JSX.Element} The single-slot panel body.
 */
export const SingleSlotContent = ({
  row,
  sessionCertified,
  onConfirmTract,
  submitting,
  onSetAmForAll,
}: SingleSlotContentProps): JSX.Element => {
  const targets: Array<SlotSubtract | null> =
    row.subtracts.length > 0 ? row.subtracts : [null];
  const anyAmEligible = targets.some((subtract) =>
    isTractAmSelectable(row, subtract)
  );

  return (
    <div className={styles.section}>
      <Button
        kind="tertiary"
        size="sm"
        className={styles.setAllButton}
        disabled={submitting || !anyAmEligible}
        onClick={onSetAmForAll}
      >
        Set AM as Evidence Source for All
      </Button>
      <p className={styles.helper}>
        Select the evidence source — AM, DO, or Custom — for each tract and
        confirm individually, or use “Set AM as Evidence Source for All” to
        apply Allocation Manager everywhere.
      </p>

      {/* Per tract: one full card per certified evidence-source act (group),
          each read-only with its own Edit (INS-9897), plus the editor card for
          the uncertified remainder — so a tract shows 1–3 cards (e.g. oil/gas
          via AM + ngl via Custom). A fully-certified tract shows only its act
          cards. The no-subtract pseudo-tract has no groups; it just renders the
          single whole-tract card. */}
      {targets.map((subtract) => {
        // Per-UAT grain (ONCALL-668): each (subtract, UAT) segment is its own
        // card / draft / session-certified bucket, so key on both.
        const key = tractKey(
          subtract?.subtractId ?? null,
          subtract?.uatId ?? null
        );
        const groups = subtract?.confirmationGroups ?? [];
        const groupCovered = new Set(groups.flatMap((group) => group.streams));
        // Show the remainder/whole-tract editor card unless every stream is
        // already covered by an act card.
        const showRemainderCard =
          groups.length === 0 ||
          row.streams.some((stream) => !groupCovered.has(stream));
        return (
          <Fragment key={key}>
            {subtract &&
              groups.map((group) => (
                <TractConfirmSection
                  key={`${key}-${group.source}-${group.streams.join('-')}`}
                  row={row}
                  subtract={subtract}
                  group={group}
                  sessionCertifiedStreams={sessionCertified[key] ?? []}
                  onConfirm={onConfirmTract}
                  submitting={submitting}
                />
              ))}
            {showRemainderCard && (
              <TractConfirmSection
                row={row}
                subtract={subtract}
                sessionCertifiedStreams={sessionCertified[key] ?? []}
                onConfirm={onConfirmTract}
                submitting={submitting}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
